<template>
    <div class="concordance-result-item">
        <div class="result-header">
            <div class="header-column">
                <span class="header-label">{{ $t('concordanceSearch.projectCode') }}:</span>
                <span class="header-value">{{ result.projectCode }}</span>
            </div>
            <div class="header-column">
                <span class="header-label">{{ $t('concordanceSearch.createdBy') }}:</span>
                <span class="header-value">{{ result.createdBy }}</span>
            </div>
            <div class="header-column">
                <span class="header-label">{{ $t('concordanceSearch.on') }}:</span>
                <span class="header-value">{{ formatDate(result.createdOn) }}</span>
            </div>
        </div>
        <div class="result-row">
            <span class="row-label">{{ $t('segmentSearch.source') }}:</span>
            <span class="row-text" v-html="highlightedSourceText"></span>
        </div>
        <div class="result-row">
            <span class="row-label">{{ $t('segmentSearch.target') }}:</span>
            <span class="row-text" v-html="highlightedTargetText"></span>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ConcordanceSearchResult } from './ConcordanceSearchModels';

export default defineComponent({
    name: 'ConcordanceSearchResultItem',
    props: {
        result: {
            type: Object as PropType<ConcordanceSearchResult>,
            required: true
        },
        searchSourceText: {
            type: String,
            default: ''
        },
        searchTargetText: {
            type: String,
            default: ''
        }
    },
    computed: {
        highlightedSourceText(): string {
            return this.highlightText(this.result.sourceText, this.searchSourceText);
        },
        highlightedTargetText(): string {
            return this.highlightText(this.result.targetText, this.searchTargetText);
        }
    },
    methods: {
        formatDate(date: Date): string {
            return new Date(date).toLocaleDateString();
        },
        highlightText(text: string, searchTerm: string): string {
            if (!searchTerm || !searchTerm.trim()) {
                return this.escapeHtml(text);
            }

            const escapedText = this.escapeHtml(text);
            const escapedSearchTerm = this.escapeRegExp(searchTerm.trim());
            const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
            
            return escapedText.replace(regex, '<mark>$1</mark>');
        },
        escapeHtml(text: string): string {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        },
        escapeRegExp(text: string): string {
            return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }
    }
});
</script>

<style lang="scss" scoped>
.concordance-result-item {
    background-color: white;
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }

    .result-header {
        display: flex;
        gap: 24px;
        padding-bottom: 8px;
        margin-bottom: 8px;
        border-bottom: 1px solid var(--el-border-color-light);

        .header-column {
            display: flex;
            gap: 4px;
        }

        .header-label {
            font-weight: 600;
            color: var(--dark-blue);
            font-size: 12px;
        }

        .header-value {
            color: var(--dark-blue);
            font-size: 12px;
        }
    }

    .result-row {
        display: flex;
        gap: 8px;
        padding: 4px 0;

        .row-label {
            font-weight: 600;
            color: var(--dark-blue);
            font-size: 14px;
            min-width: 60px;
        }

        .row-text {
            color: var(--dark-blue);
            font-size: 14px;

            :deep(mark) {
                background-color: var(--yellow);
                color: var(--dark-blue);
                font-weight: 600;
                padding: 2px 4px;
                border-radius: 3px;
            }
        }
    }
}
</style>
